import React from "react";
import { Card, Button, Typography, Space, Divider, Spin } from "antd";
import { WindowsOutlined } from "@ant-design/icons";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate } from "react-router-dom";
import { loginRequest } from "../auth/msalConfig";

const { Title, Text } = Typography;

/**
 * Login page rendered for unauthenticated users.
 * Initiates the Microsoft Entra ID OAuth2 redirect flow via MSAL.
 */
export function LoginPage(): React.JSX.Element {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show spinner while MSAL is processing redirect
  if (
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.AcquireToken ||
    inProgress === InteractionStatus.Startup
  ) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Authentification en cours..." />
      </div>
    );
  }

  const isLoading = inProgress !== "none";

  function handleSignIn(): void {
    instance.loginRedirect(loginRequest).catch((error: unknown) => {
      console.error("Login redirect failed:", error);
    });
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}
        styles={{ body: { padding: 40 } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Branding */}
          <div style={{ textAlign: "center" }}>
            <Title level={2} style={{ marginBottom: 4 }}>
              QB Financial Dashboard
            </Title>
            <Text type="secondary">
              Sign in to access your financial reports
            </Text>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Sign in button */}
          <Button
            type="primary"
            size="large"
            block
            icon={<WindowsOutlined />}
            onClick={handleSignIn}
            loading={isLoading}
            disabled={isLoading}
            style={{ height: 48, fontSize: 15 }}
          >
            Sign in with Microsoft
          </Button>

          <Text
            type="secondary"
            style={{ textAlign: "center", fontSize: 12, display: "block" }}
          >
            Authentication is managed by Microsoft Entra ID.
            <br />
            Only authorized accounts may access this application.
          </Text>
        </Space>
      </Card>
    </div>
  );
}
